import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import type { Post } from '../types';

type CreatePostFabProps = {
  onCreate: (category?: Post['category']) => void;
};

const CreatePostFab: React.FC<CreatePostFabProps> = ({ onCreate }) => {
  const [open, setOpen] = useState(false);

  const actions = useMemo(
    () =>
      [
        { category: 'announcement' as const, label: 'Anunț', icon: 'Megaphone' },
        { category: 'discussion' as const, label: 'Discuție', icon: 'MessageCircle' },
        { category: 'event' as const, label: 'Eveniment', icon: 'CalendarPlus' },
        { category: 'resource' as const, label: 'Resursă', icon: 'FileText' },
        { category: 'achievement' as const, label: 'Realizare', icon: 'Award' },
      ] satisfies Array<{
        category: Post['category'];
        label: string;
        icon: string;
      }>,
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div className="sm:hidden">
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-[1030] bg-black/20"
          aria-label="Închide meniul"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div className="fixed bottom-5 right-5 z-[1035] flex flex-col items-end gap-3 pb-[env(safe-area-inset-bottom)]">
        <div
          className={[
            'flex flex-col items-end gap-2 transition-all duration-200',
            open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
          ].join(' ')}
        >
          {actions.map((a) => (
            <button
              key={a.category}
              type="button"
              className="flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                onCreate(a.category);
              }}
            >
              <span className="px-3 py-2 rounded-full bg-card/95 border border-border shadow-sm text-sm font-medium text-foreground">
                {a.label}
              </span>
              <span className="h-11 w-11 rounded-full bg-card border border-border shadow-md flex items-center justify-center">
                <Icon name={a.icon} size={20} className="text-primary" />
              </span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="h-14 w-14 rounded-full bg-gradient-to-r from-primary to-indigo-500 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Creează postare"
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name={open ? 'X' : 'Plus'} size={24} />
        </button>
      </div>
    </div>
  );
};

export default CreatePostFab;
